import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { X, ImageOff } from "lucide-react";

const TABS = ["All", "Our Fleet", "Warehousing", "On-Road Operations"];

export const galleryImgSrc = (g) => g.image_url || `${API_BASE}/gallery/image/${g.id}`;

export default function PublicGallery() {
  const [images, setImages] = useState([]);
  const [tab, setTab] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/gallery")
      .then((r) => setImages(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = tab === "All" ? images : images.filter((g) => g.category === tab);

  return (
    <div className="py-16 px-6 lg:px-16 max-w-7xl mx-auto w-full" data-testid="public-gallery-page">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Our Operations</div>
      <h1 className="font-['Outfit'] mt-3 text-4xl sm:text-5xl font-black tracking-tight text-slate-900">Gallery</h1>
      <p className="mt-4 text-base text-slate-600 max-w-xl">
        A look at our fleet, warehousing facilities and on-road operations across India.
      </p>

      {/* Filter tabs */}
      <div className="mt-10 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`gallery-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-colors duration-200 ${
              tab === t ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900 bg-white border border-slate-200"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Masonry grid */}
      {loading ? (
        <div className="mt-12 text-slate-500 text-sm">Loading gallery…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-16 flex flex-col items-center text-slate-400 gap-3">
          <ImageOff size={40} />
          <div className="text-sm font-medium">No images in this category yet.</div>
        </div>
      ) : (
        <div className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
          {filtered.map((g) => (
            <button
              key={g.id}
              onClick={() => setLightbox(g)}
              data-testid={`gallery-item-${g.id}`}
              className="group relative block w-full break-inside-avoid rounded-lg overflow-hidden text-left focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <img src={galleryImgSrc(g)} alt={g.title} loading="lazy"
                className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-transparent p-5 pt-16 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="inline-block text-[10px] font-bold uppercase tracking-widest bg-orange-500 text-white px-2.5 py-1 rounded">
                  {g.category}
                </span>
                <div className="font-['Outfit'] mt-2 text-white font-semibold">{g.title}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button className="absolute top-5 right-5 text-white/80 hover:text-white p-2" data-testid="lightbox-close-btn" aria-label="Close">
            <X size={28} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={galleryImgSrc(lightbox)} alt={lightbox.title} className="w-full max-h-[80vh] object-contain rounded-lg" />
            <div className="mt-4 flex items-center gap-3">
              <span className="text-[10px] font-bold uppercase tracking-widest bg-orange-500 text-white px-2.5 py-1 rounded">
                {lightbox.category}
              </span>
              <div className="font-['Outfit'] text-white text-lg font-semibold">{lightbox.title}</div>
            </div>
            {lightbox.description && <p className="mt-2 text-sm text-slate-300">{lightbox.description}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
